import { useContext } from "react";
import { ImportConfigAction } from "../actions";
import { AccessPointProcessingGroup, AccessPointProcessingStep, ImportConfigSettings, License } from "../models";
import { ProviderNetworkImportContext } from "../providerNetworksImportContext";
import ImportConfigService from "../services/importConfigService";
import ProviderNetworkImportService from "../services/providerNetworkImportService";
import { ImportGroupState, ImportStepState } from "../state";

interface UseImportConfigReturnType {
  addGroups: (groups: AccessPointProcessingGroup[], license?: License) => Promise<void>;
  getConfiguredGroups: () => AccessPointProcessingGroup[];
  getGroup: (group: AccessPointProcessingGroup) => ImportGroupState;
  getStep: (step: AccessPointProcessingStep) => ImportStepState;
  hasConfiguredGroups: () => boolean;
  isConfigurationLoading: boolean;
  loadConfiguration: (networkId: number, versionId?: number) => Promise<void>;
  removeGroup: (group: AccessPointProcessingGroup) => Promise<void>;
  runAll: () => Promise<void>;
  runGroup: (group: AccessPointProcessingGroup) => Promise<void>;
  setStepEdited: (step: AccessPointProcessingStep) => void;
  settings: ImportConfigSettings;
  toggleAutoActivate: () => Promise<void>;
  toggleAutoComplete: () => Promise<void>;
  toggleAutoRun: () => Promise<void>;
  toggleStep: (step: AccessPointProcessingStep) => Promise<void>;
}

export function useImportConfig(): UseImportConfigReturnType {
  const { configDispatch, config } = useContext(ProviderNetworkImportContext);
  const importService = new ProviderNetworkImportService();
  const importConfigService = new ImportConfigService();
  const setStepEdited = (step: AccessPointProcessingStep) => configDispatch({ step, type: ImportConfigAction.SetStepEdited });

  const toggleStep = async (step: AccessPointProcessingStep) => {
    await importConfigService.changeStepEnabled(config.steps[step].id, !config.steps[step].enabled);
    configDispatch({ step, type: ImportConfigAction.ToggleStep });
  };

  const loadConfiguration = async (networkId: number, versionId?: number) => {
    configDispatch({ networkId, type: ImportConfigAction.LoadConfiguration, versionId });
    const result = await importConfigService.getConfiguration(networkId, versionId);
    configDispatch({ result, type: ImportConfigAction.LoadConfigurationSuccess });
  };

  const toggleAutoComplete = async () => {
    const request: ImportConfigSettings = {
      ...config.settings,
      autoComplete: !config.settings.autoComplete,
    };
    configDispatch({ type: ImportConfigAction.ToggleAutoComplete });
    await importConfigService.changeConfigSettings(config.id, request);
  };

  const toggleAutoActivate = async () => {
    const request: ImportConfigSettings = {
      ...config.settings,
      autoActivate: !config.settings.autoActivate,
    };
    configDispatch({ type: ImportConfigAction.ToggleAutoActivate });
    await importConfigService.changeConfigSettings(config.id, request);
  };

  const toggleAutoRun = async () => {
    const request: ImportConfigSettings = {
      ...config.settings,
      autoRun: !config.settings.autoRun,
    };
    configDispatch({ type: ImportConfigAction.ToggleAutoRun });
    await importConfigService.changeConfigSettings(config.id, request);
  };

  const addGroups = async (groups: AccessPointProcessingGroup[], license?: License) => {
    const response = await importConfigService.addGroupsToConfig(groups, config.providerNetworkId, null, license);
    configDispatch({ response, type: ImportConfigAction.AddGroupsSuccess });
  };

  const removeGroup = async (group: AccessPointProcessingGroup) => {
    await importConfigService.removeGroup(group, config.providerNetworkId);
    configDispatch({ group, type: ImportConfigAction.RemoveGroupSuccess });
  };

  const runAll = async () => {
    await importService.runGroups(getConfiguredGroups(), config.versionId);
  };

  const runGroup = async (group: AccessPointProcessingGroup) => {
    await importService.runGroups([group], config.versionId);
  };

  const getStep = (step: AccessPointProcessingStep) => {
    return config.steps[step];
  };

  const getGroup = (group: AccessPointProcessingGroup) => {
    return config.groups[group];
  };

  const getConfiguredGroups = () => {
    return Object.keys(config.groups) as AccessPointProcessingGroup[];
  };

  const hasConfiguredGroups = () => {
    return getConfiguredGroups().length > 0;
  };

  const isConfigurationLoading = config.isLoading;
  const settings = config.settings;

  return {
    addGroups,
    getConfiguredGroups,
    getGroup,
    getStep,
    hasConfiguredGroups,
    isConfigurationLoading,
    loadConfiguration,
    removeGroup,
    runAll,
    runGroup,
    setStepEdited,
    settings,
    toggleAutoActivate,
    toggleAutoComplete,
    toggleAutoRun,
    toggleStep,
  };
}
